import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, AlertTriangle, Phone, FileText, Wrench, ShieldCheck } from 'lucide-react';
import { QuoteForm } from '../components/QuoteForm';
export function StormDamagePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const steps = [{
    icon: Phone,
    title: '1. Emergency Tarping & Assessment',
    desc: "Call us immediately. We'll dispatch a team to tarp exposed areas to prevent further interior water damage and conduct a thorough exterior inspection."
  }, {
    icon: FileText,
    title: '2. Insurance Claim Assistance',
    desc: 'We document all damage with photos and detailed reports. We can meet with your insurance adjuster on-site to ensure all damage is properly accounted for.'
  }, {
    icon: ShieldCheck,
    title: '3. Material Selection & Approval',
    desc: "Once the claim is approved, we help you select replacement materials that match or upgrade your home's previous exterior."
  }, {
    icon: Wrench,
    title: '4. Professional Restoration',
    desc: 'Our expert crews quickly and safely restore your roof, siding, or gutters to pre-storm condition, backed by our workmanship warranty.'
  }];
  return <div className="w-full bg-white" data-id="element-985">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center pt-20 pb-16 overflow-hidden" data-id="element-986">
        <div className="absolute inset-0 z-0" data-id="element-987">
          <img src="https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=1920&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-988" />
          <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-989"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/40 z-[2]" data-id="element-990"></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-991">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-992">
            {/* Breadcrumbs */}
            <nav className="flex items-center text-sm text-gray-300 font-body mb-6" data-id="element-993">
              <Link to="/" className="hover:text-brand-blue transition-colors" data-id="element-994">
                Home
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-995" />
              <span className="text-white font-semibold" data-id="element-996">Storm Damage</span>
            </nav>

            <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-1.5 rounded-full font-body text-sm font-bold mb-6 animate-pulse" data-id="element-997">
              <AlertTriangle size={16} data-id="element-998" />
              Emergency Services Available
            </div>

            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6 drop-shadow-lg" data-id="element-999">
              Michigan <span className="text-brand-blue" data-id="element-1000">Storm Damage</span> &
              Roof Repair
            </h1>
            <p className="font-body text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed drop-shadow-md" data-id="element-1001">
              High winds, hail, and severe storms can devastate your roof and
              siding. We provide emergency tarping, thorough inspections, and
              full insurance claim assistance.
            </p>

            <a href="tel:5862033701" className="inline-flex items-center gap-3 bg-white text-red-600 font-heading font-black text-xl py-4 px-8 rounded-lg hover:bg-gray-100 transition-colors shadow-xl" data-id="element-1002">
              <Phone size={24} data-id="element-1003" />
              CALL NOW: (586) 203-3701
            </a>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50" data-id="element-1004">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1005">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20" data-id="element-1006">
            <div className="w-full lg:w-7/12" data-id="element-1007">
              <h2 className="font-heading font-bold text-3xl text-brand-gray-dark mb-6" data-id="element-1008">
                Don't Wait to Address Storm Damage
              </h2>
              <div className="w-16 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-1009"></div>

              <div className="prose prose-lg text-brand-gray font-body mb-12" data-id="element-1010">
                <p className="mb-6" data-id="element-1011">
                  Even minor storm damage can lead to catastrophic interior
                  water damage if left unaddressed. Missing shingles, dented
                  siding, or torn gutters compromise your home's protective
                  envelope.
                </p>
                <p data-id="element-1012">
                  At Moceri Residential Services, we specialize in storm damage
                  restoration. We know how stressful it is to deal with property
                  damage, which is why we handle the heavy lifting—from
                  emergency tarping to meeting with your insurance adjuster.
                </p>
              </div>

              <h3 className="font-heading font-bold text-2xl text-brand-gray-dark mb-8" data-id="element-1013">
                Our Restoration Process
              </h3>

              <div className="space-y-6" data-id="element-1014">
                {steps.map((step, index) => <div key={index} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex gap-6" data-id="element-1015">
                    <div className="w-14 h-14 bg-brand-blue/10 rounded-full flex items-center justify-center shrink-0" data-id="element-1016">
                      <step.icon size={28} className="text-brand-blue" data-id="element-1017" />
                    </div>
                    <div data-id="element-1018">
                      <h4 className="font-heading font-bold text-xl text-brand-gray-dark mb-2" data-id="element-1019">
                        {step.title}
                      </h4>
                      <p className="font-body text-brand-gray" data-id="element-1020">{step.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Right Form */}
            <div className="w-full lg:w-5/12" data-id="element-1021">
              <div className="sticky top-28" data-id="element-1022">
                <div className="bg-brand-blue text-white p-6 rounded-t-xl text-center" data-id="element-1023">
                  <h3 className="font-heading font-bold text-xl" data-id="element-1024">
                    Request an Inspection
                  </h3>
                  <p className="font-body text-sm text-blue-100 mt-1" data-id="element-1025">
                    We'll respond as quickly as possible.
                  </p>
                </div>
                <div className="bg-white border-x border-b border-gray-100 rounded-b-xl shadow-xl" data-id="element-1026">
                  <QuoteForm data-id="element-1027" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}